import React from "react";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Alert,
  useTheme,
  IconButton,
  Divider,
} from "@mui/material";
import { useThemeSettings } from "../context/ThemeContext";
import { useAuth } from "../context/AuthContext";
import { API_BASE_URL } from "../config/apiKey.js";
import SubscriptionStatus from "../components/SubscriptionStatus";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Delete from "@mui/icons-material/Delete";

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography component="div">{children}</Typography>
        </Box>
      )}
    </div>
  );
}

export default function SettingsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = React.useState(0);
  const [defaultProject, setDefaultProject] = React.useState("");
  const [confirmLongTasks, setConfirmLongTasks] = React.useState(false);
  const [showAdvanced, setShowAdvanced] = React.useState(false);
  const [notebooks, setNotebooks] = React.useState([]);
  const [selectedNotebook, setSelectedNotebook] = React.useState(null);
  const [notebookContent, setNotebookContent] = React.useState("");
  const [notebookTitle, setNotebookTitle] = React.useState("");
  const [savedMessage, setSavedMessage] = React.useState("");
  const { mode, setMode, highContrast, setHighContrast } = useThemeSettings();
  const theme = useTheme();
  const isDark = mode === "dark";
  const { user, token } = useAuth();
  const isAdmin = !!user?.isAdmin;
  const [blockedUsers, setBlockedUsers] = React.useState([]);
  const [adminError, setAdminError] = React.useState("");
  const [adminLoading, setAdminLoading] = React.useState(false);

  const handleChange = (_event, newValue) => {
    setTab(newValue);
  };

  React.useEffect(() => {
    try {
      const raw = window.localStorage.getItem("exi-user-preferences");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (typeof parsed.showAdvanced === "boolean") {
          setShowAdvanced(parsed.showAdvanced);
        }
        if (typeof parsed.confirmLongTasks === "boolean") {
          setConfirmLongTasks(parsed.confirmLongTasks);
        }
        if (typeof parsed.defaultProject === "string") {
          setDefaultProject(parsed.defaultProject);
        }
      }
    } catch {
      // ignore
    }
  }, []);

  // Fetch notebooks from backend
  const fetchNotebooks = async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/notebooks`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.status === 502) {
        console.warn("Notebooks service is temporarily unavailable (502). Falling back to local storage.");
        // Fallback to local storage if server is down
        const savedNotebooks = window.localStorage.getItem("exi-notebooks");
        if (savedNotebooks) {
          setNotebooks(JSON.parse(savedNotebooks));
        }
        return;
      }

      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        if (response.ok) {
          const data = await response.json();
          setNotebooks(data);
          // After successful fetch, check if we need to migrate local notebooks
          await migrateLocalNotebooks(data);
        }
      } else {
        console.error("Fetch notebooks: Server returned non-JSON response");
      }
    } catch (error) {
      console.error("Error fetching notebooks:", error);
    }
  };

  const migrateLocalNotebooks = async (serverNotebooks) => {
    try {
      const savedNotebooks = window.localStorage.getItem("exi-notebooks");
      const oldNotebook = window.localStorage.getItem("exi-notebook");
      
      let localNotebooks = [];
      if (savedNotebooks) {
        try {
          localNotebooks = JSON.parse(savedNotebooks);
        } catch {
          if (oldNotebook) {
            localNotebooks = [{ title: "My Notebook", content: oldNotebook }];
          }
        }
      } else if (oldNotebook) {
        localNotebooks = [{ title: "My Notebook", content: oldNotebook }];
      }

      if (localNotebooks.length > 0) {
        console.log("Migrating local notebooks to server...");
        for (const local of localNotebooks) {
          // Check if it already exists by title
          if (!serverNotebooks.find(nb => nb.title === local.title)) {
            await fetch(`${API_BASE_URL}/api/notebooks`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({ title: local.title, content: local.content }),
            });
          }
        }
        // Clear local storage after migration attempt
        window.localStorage.removeItem("exi-notebooks");
        window.localStorage.removeItem("exi-notebook");
        // Refresh from server
        const response = await fetch(`${API_BASE_URL}/api/notebooks`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          setNotebooks(await response.json());
        }
      }
    } catch (error) {
      console.error("Migration error:", error);
    }
  };

  React.useEffect(() => {
    if (token) {
      fetchNotebooks();
    }
  }, [token]);

  const persistPreferences = (next = {}) => {
    try {
      const current = {
        showAdvanced,
        confirmLongTasks,
        defaultProject,
        ...next,
      };
      window.localStorage.setItem(
        "exi-user-preferences",
        JSON.stringify(current)
      );
    } catch {
      // ignore
    }
  };

  const handleSaveNotebook = async () => {
    if (!notebookTitle.trim()) {
      setSavedMessage("Please enter a title for your notebook.");
      setTimeout(() => setSavedMessage(""), 3000);
      return;
    }

    try {
      let response;
      if (selectedNotebook) {
        // Update existing notebook on server
        response = await fetch(`${API_BASE_URL}/api/notebooks/${selectedNotebook.id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ title: notebookTitle.trim(), content: notebookContent }),
        });
      } else {
        // Create new notebook on server
        response = await fetch(`${API_BASE_URL}/api/notebooks`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ title: notebookTitle.trim(), content: notebookContent }),
        });
      }

      if (response.ok) {
        const savedNotebook = await response.json();
        
        // Update local state
        if (selectedNotebook) {
          setNotebooks(notebooks.map(nb => nb.id === savedNotebook.id ? savedNotebook : nb));
        } else {
          setNotebooks([...notebooks, savedNotebook]);
        }
        
        setSelectedNotebook(savedNotebook);
        setSavedMessage("Notebook saved successfully.");
      } else {
        const errorData = await response.json();
        setSavedMessage(`Error: ${errorData.error || "Failed to save"}`);
      }
      setTimeout(() => setSavedMessage(""), 3000);
    } catch (error) {
      console.error("Save notebook error:", error);
      setSavedMessage("Could not save notebook. Check connection.");
      setTimeout(() => setSavedMessage(""), 3000);
    }
  };

  const handleNewNotebook = () => {
    setSelectedNotebook(null);
    setNotebookTitle("");
    setNotebookContent("");
  };

  const handleSelectNotebook = (notebook) => {
    setSelectedNotebook(notebook);
    setNotebookTitle(notebook.title);
    setNotebookContent(notebook.content);
  };

  const handleDeleteNotebook = async (notebookId) => {
    if (window.confirm("Are you sure you want to delete this notebook?")) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/notebooks/${notebookId}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.ok) {
          setNotebooks(notebooks.filter(nb => nb.id !== notebookId));
          
          if (selectedNotebook && selectedNotebook.id === notebookId) {
            setSelectedNotebook(null);
            setNotebookTitle("");
            setNotebookContent("");
          }
          
          setSavedMessage("Notebook deleted.");
        } else {
          setSavedMessage("Failed to delete notebook.");
        }
      } catch (error) {
        console.error("Delete notebook error:", error);
        setSavedMessage("Failed to delete notebook. Check connection.");
      }
      setTimeout(() => setSavedMessage(""), 3000);
    }
  };

  const fetchBlockedUsers = async () => {
    if (!token || !isAdmin) return;
    setAdminError("");
    setAdminLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/blocked-users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.status === 502) {
        setAdminError("Admin service is temporarily unavailable (502). Please try again in a few seconds.");
        setAdminLoading(false);
        return;
      }

      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        const data = await response.json();
        if (response.ok) {
          setBlockedUsers(data.users || []);
        } else {
          setAdminError(data.error || "Failed to load blocked users");
        }
      } else {
        setAdminError("Server returned an invalid response format. The service might be starting up.");
      }
    } catch (e) {
      setAdminError(e.message || "Failed to load blocked users");
    } finally {
      setAdminLoading(false);
    }
  };

  const handleUnlockUser = async (userId) => {
    if (!token || !isAdmin) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/unlock-user`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ userId }),
      });
      const data = await response.json();
      if (response.ok) {
        setBlockedUsers((prev) => prev.filter((u) => u.id !== userId));
      } else {
        setAdminError(data.error || "Failed to unlock user");
      }
    } catch (e) {
      setAdminError(e.message || "Failed to unlock user");
    }
  };

  React.useEffect(() => {
    if (isAdmin) {
      fetchBlockedUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#ffffff",
        color: "#0f172a",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        py: { xs: 2, md: 6 },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "95%",
          maxWidth: 1000,
          bgcolor: "#ffffff",
          borderRadius: 4,
          border: "1px solid #e2e8f0",
          overflow: "hidden",
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.05)",
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 3,
            borderBottom: "1px solid #e2e8f0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: "#f8fafc",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <IconButton
              onClick={() => navigate("/dashboard")}
              sx={{
                color: "#1e3a8a",
                bgcolor: "#ffffff",
                border: "1px solid #e2e8f0",
                "&:hover": {
                  bgcolor: "#f1f5f9",
                },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: "#1e3a8a" }}>
                Settings
              </Typography>
              <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 500 }}>
                Manage your account and app preferences.
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" } }}>
          {/* Left tabs */}
          <Box
            sx={{
              borderRight: { xs: "none", md: "1px solid #e2e8f0" },
              borderBottom: { xs: "1px solid #e2e8f0", md: "none" },
              minWidth: 240,
              bgcolor: "#f8fafc",
            }}
          >
            <Tabs
              orientation={window.innerWidth < 900 ? "horizontal" : "vertical"}
              variant="scrollable"
              value={tab}
              onChange={handleChange}
              aria-label="Settings sections"
              sx={{
                "& .MuiTab-root": {
                  alignItems: "flex-start",
                  textTransform: "none",
                  color: "#64748b",
                  fontWeight: 600,
                  fontSize: "0.95rem",
                  py: 2,
                  px: 3,
                  minHeight: 0,
                },
                "& .Mui-selected": {
                  color: "#1e3a8a",
                  bgcolor: "#ffffff",
                },
                "& .MuiTabs-indicator": {
                  left: 0,
                  width: "4px",
                  backgroundColor: "#1e3a8a",
                  borderRadius: "0 4px 4px 0",
                },
              }}
            >
              <Tab label="User Preferences" />
              <Tab label="Appearance" />
              <Tab label="Subscriptions" />
              <Tab label="Notebooks" />
              <Tab label="Help & Support" />
              {isAdmin && <Tab label="Admin Console" />}
            </Tabs>
          </Box>

          {/* Right content */}
          <Box sx={{ flex: 1, bgcolor: "#ffffff", minHeight: 500 }}>
            <TabPanel value={tab} index={0}>
              <Typography variant="h6" sx={{ mb: 3, color: "#1e3a8a", fontWeight: 700 }}>
                User Preferences
              </Typography>
              <List sx={{ "& .MuiListItem-root": { px: 0, py: 2 } }}>
                <ListItem sx={{ flexDirection: "column", alignItems: "flex-start" }}>
                  <FormControlLabel
                    control={
                      <Switch
                        color="primary"
                        checked={showAdvanced}
                        onChange={(e) => {
                          setShowAdvanced(e.target.checked);
                          persistPreferences({ showAdvanced: e.target.checked });
                        }}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: "#0f172a" }}>
                          Advanced Options
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#64748b" }}>
                          Enable detailed AI model parameters and system debug info.
                        </Typography>
                      </Box>
                    }
                    sx={{ width: "100%", m: 0, gap: 2 }}
                  />
                </ListItem>
                <Divider />
                <ListItem sx={{ flexDirection: "column", alignItems: "flex-start" }}>
                  <FormControlLabel
                    control={
                      <Switch
                        color="primary"
                        checked={confirmLongTasks}
                        onChange={(e) => {
                          setConfirmLongTasks(e.target.checked);
                          persistPreferences({
                            confirmLongTasks: e.target.checked,
                          });
                        }}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: "#0f172a" }}>
                          Task Confirmation
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#64748b" }}>
                          Ask before starting complex or long-running AI processes.
                        </Typography>
                      </Box>
                    }
                    sx={{ width: "100%", m: 0, gap: 2 }}
                  />
                </ListItem>
                <Divider />
                <ListItem>
                  <Box sx={{ width: "100%", mt: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: "#0f172a", mb: 1.5 }}>
                      Default Project
                    </Typography>
                    <TextField
                      placeholder="Enter project name"
                      fullWidth
                      variant="outlined"
                      size="medium"
                      value={defaultProject}
                      onChange={(e) => {
                        setDefaultProject(e.target.value);
                        persistPreferences({ defaultProject: e.target.value });
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          bgcolor: "#f8fafc",
                          "& fieldset": { borderColor: "#e2e8f0" },
                          "&:hover fieldset": { borderColor: "#cbd5e1" },
                          "&.Mui-focused fieldset": { borderColor: "#1e3a8a" },
                        },
                      }}
                    />
                    <Typography variant="caption" sx={{ color: "#94a3b8", mt: 1, display: "block" }}>
                      New chats will be automatically assigned to this project if none is selected.
                    </Typography>
                  </Box>
                </ListItem>
              </List>
            </TabPanel>

            <TabPanel value={tab} index={1}>
              <Typography variant="h6" sx={{ mb: 3, color: "#1e3a8a", fontWeight: 700 }}>
                Appearance
              </Typography>
              <List sx={{ "& .MuiListItem-root": { px: 0, py: 2 } }}>
                <ListItem sx={{ flexDirection: "column", alignItems: "flex-start" }}>
                  <FormControlLabel
                    control={
                      <Switch
                        color="primary"
                        checked={mode === "dark"}
                        onChange={(e) =>
                          setMode(e.target.checked ? "dark" : "light")
                        }
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: "#0f172a" }}>
                          Dark Mode
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#64748b" }}>
                          Use a dark color palette for late-night sessions.
                        </Typography>
                      </Box>
                    }
                    sx={{ width: "100%", m: 0, gap: 2 }}
                  />
                </ListItem>
                <Divider />
                <ListItem sx={{ flexDirection: "column", alignItems: "flex-start" }}>
                  <FormControlLabel
                    control={
                      <Switch
                        color="primary"
                        checked={highContrast}
                        onChange={(e) => setHighContrast(e.target.checked)}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: "#0f172a" }}>
                          High Contrast
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#64748b" }}>
                          Enhance visibility with sharper colors and borders.
                        </Typography>
                      </Box>
                    }
                    sx={{ width: "100%", m: 0, gap: 2 }}
                  />
                </ListItem>
              </List>
            </TabPanel>

            <TabPanel value={tab} index={2}>
              <Typography variant="h6" sx={{ mb: 3, color: "#1e3a8a", fontWeight: 700 }}>
                Subscriptions
              </Typography>
              <SubscriptionStatus />
            </TabPanel>

            <TabPanel value={tab} index={3}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                <Box>
                  <Typography variant="h6" sx={{ color: "#1e3a8a", fontWeight: 700 }}>
                    My Notebooks
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5, fontWeight: 500 }}>
                    Personal space for prompts and project notes.
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  onClick={handleNewNotebook}
                  sx={{
                    bgcolor: "#1e3a8a",
                    fontWeight: 700,
                    textTransform: "none",
                    borderRadius: "8px",
                    px: 3,
                    "&:hover": { bgcolor: "#1e40af" },
                  }}
                >
                  + New
                </Button>
              </Box>
              
              {savedMessage && (
                <Alert 
                  severity={savedMessage.includes("Error") ? "error" : "success"}
                  sx={{ mb: 3, borderRadius: "8px" }}
                  onClose={() => setSavedMessage("")}
                >
                  {savedMessage}
                </Alert>
              )}

              <Box sx={{ display: "flex", gap: 3, flexDirection: { xs: "column", md: "row" } }}>
                {/* Notebooks List */}
                <Box
                  sx={{
                    width: { xs: "100%", md: 260 },
                    bgcolor: "#f8fafc",
                    borderRadius: 2,
                    p: 1,
                    maxHeight: 450,
                    overflowY: "auto",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  {notebooks.length === 0 ? (
                    <Typography variant="body2" sx={{ color: "#94a3b8", textAlign: "center", py: 4 }}>
                      Empty.
                    </Typography>
                  ) : (
                    <List dense>
                      {notebooks.map((notebook) => (
                        <ListItem
                          key={notebook.id}
                          button
                          onClick={() => handleSelectNotebook(notebook)}
                          selected={selectedNotebook?.id === notebook.id}
                          sx={{
                            mb: 0.5,
                            borderRadius: 1.5,
                            "&.Mui-selected": {
                              bgcolor: "#ffffff",
                              boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                              border: "1px solid #e2e8f0",
                            },
                          }}
                        >
                          <ListItemText
                            primary={notebook.title}
                            primaryTypographyProps={{ fontWeight: 600, color: "#0f172a", noWrap: true }}
                            secondary={new Date(notebook.updatedAt || notebook.createdAt).toLocaleDateString()}
                          />
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteNotebook(notebook.id);
                            }}
                            sx={{ color: "#ef4444" }}
                          >
                            <Delete sx={{ fontSize: "18px" }} />
                          </IconButton>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>

                {/* Notebook Editor */}
                <Box sx={{ flex: 1 }}>
                  <TextField
                    placeholder="Notebook Title"
                    fullWidth
                    variant="outlined"
                    size="small"
                    value={notebookTitle}
                    onChange={(e) => setNotebookTitle(e.target.value)}
                    sx={{
                      mb: 2,
                      "& .MuiOutlinedInput-root": {
                        bgcolor: "#ffffff",
                        fontWeight: 700,
                        fontSize: "1.1rem",
                        "& fieldset": { borderColor: "#e2e8f0" },
                      },
                    }}
                  />

                  <TextField
                    multiline
                    minRows={12}
                    maxRows={20}
                    fullWidth
                    placeholder="Start writing..."
                    variant="outlined"
                    value={notebookContent}
                    onChange={(e) => setNotebookContent(e.target.value)}
                    sx={{
                      mb: 2,
                      "& .MuiOutlinedInput-root": {
                        bgcolor: "#ffffff",
                        "& fieldset": { borderColor: "#e2e8f0" },
                      },
                    }}
                  />
                  
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 600 }}>
                      {notebookContent.length} chars
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={handleSaveNotebook}
                      disabled={!notebookTitle.trim()}
                      sx={{
                        bgcolor: "#1e3a8a",
                        fontWeight: 700,
                        textTransform: "none",
                        borderRadius: "8px",
                        boxShadow: "none",
                        "&:hover": { bgcolor: "#1e40af", boxShadow: "none" },
                      }}
                    >
                      {selectedNotebook ? "Update" : "Save"}
                    </Button>
                  </Box>
                </Box>
              </Box>
            </TabPanel>

            <TabPanel value={tab} index={4}>
              <Typography variant="h6" sx={{ mb: 3, color: "#1e3a8a", fontWeight: 700 }}>
                Help Center
              </Typography>
              
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {[
                  { q: "How to use projects?", a: "Create projects in the sidebar to group related chats. AI will remember context within the same project." },
                  { q: "Data privacy?", a: "Your chats are private and used only to improve your experience. We do not sell your data." },
                  { q: "Model differences?", a: "GPT-4 is best for logic, Claude for creative writing, and mini models for quick answers." }
                ].map((faq, i) => (
                  <Box key={i} sx={{ p: 2, bgcolor: "#f8fafc", borderRadius: 2, border: "1px solid #e2e8f0" }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1e3a8a", mb: 0.5 }}>
                      {faq.q}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#475569", lineHeight: 1.6 }}>
                      {faq.a}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Box sx={{ mt: 4, p: 3, bgcolor: "rgba(30, 58, 138, 0.05)", borderRadius: 3, border: "1px dashed #1e3a8a", textAlign: "center" }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#1e3a8a", mb: 1 }}>
                  Need more help?
                </Typography>
                <Button 
                  variant="outlined" 
                  onClick={() => navigate("/contact")}
                  sx={{ mt: 1, borderColor: "#1e3a8a", color: "#1e3a8a", fontWeight: 700, borderRadius: "8px" }}
                >
                  Contact Support
                </Button>
              </Box>
            </TabPanel>

            {isAdmin && (
              <TabPanel value={tab} index={5}>
                <Typography variant="h6" sx={{ mb: 2, color: "#1e3a8a", fontWeight: 700 }}>
                  Admin Console
                </Typography>
                {adminError && <Alert severity="error" sx={{ mb: 2 }}>{adminError}</Alert>}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={fetchBlockedUsers}
                  disabled={adminLoading}
                  sx={{ mb: 3, borderRadius: "8px", fontWeight: 600 }}
                >
                  {adminLoading ? "Refreshing..." : "Refresh User List"}
                </Button>
                {blockedUsers.length === 0 ? (
                  <Typography variant="body2" sx={{ color: "#94a3b8", fontStyle: "italic" }}>
                    No users currently blocked.
                  </Typography>
                ) : (
                  <List>
                    {blockedUsers.map((u) => (
                      <ListItem
                        key={u.id}
                        sx={{
                          bgcolor: "#fef2f2",
                          borderRadius: 2,
                          mb: 1,
                          border: "1px solid #fee2e2",
                        }}
                      >
                        <ListItemText
                          primary={u.name}
                          primaryTypographyProps={{ fontWeight: 700 }}
                          secondary={`${u.email} • Blocked: ${u.blocked_at || "N/A"}`}
                        />
                        <Button
                          variant="contained"
                          color="error"
                          size="small"
                          onClick={() => handleUnlockUser(u.id)}
                          sx={{ borderRadius: "6px", fontWeight: 700 }}
                        >
                          Unlock
                        </Button>
                      </ListItem>
                    ))}
                  </List>
                )}
              </TabPanel>
            )}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}


